import React, { useEffect } from "react";
import {
    Box,
    Divider,
    Grid,
    Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import {
    BottomGridMiddleContainerField,
    OuterBodyGrid,
    TopGrid,
    LabelHeader,
    renderRowStyle
} from "../styles/TabsStyle";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import ReadOnlyField from "../../../components/formComponents/ReadOnlyField";
import { decryptId, getFormattedPhoneNo, getValue } from "../../../utils";
import { RequestDetailsModel } from "../../../models/AuthorizationDetailsTab";

export interface PropsFromState {
    requestDetailsState: RequestDetailsModel;
}

export interface PropsFromDispatch {
    getRequestDetails: (referralId: number) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const RequestDetails: React.FC<AllProps> = ({ getRequestDetails, requestDetailsState }) => {

    const { authNo } = useParams();
    const [referralId, setReferralId] = React.useState<number | null>(null);

    useEffect(() => {
        const loadReferralId = async () => {
            if (!authNo) {
                setReferralId(null);
                return;
            }

            const decryptedId = await decryptId(authNo);
            const parsed = Number(decryptedId);
            setReferralId(Number.isFinite(parsed) ? parsed : null);
        };

        void loadReferralId().catch(() => setReferralId(null));
    }, [authNo]);

    const sections = [
        {
            title: "REFERRAL SOURCE",
            contact: [
                { label: "Name:", value: getValue(requestDetailsState, "referralSource.name") },
                { label: "NPI:", value: getValue(requestDetailsState, "referralSource.npi") },
                { label: "Phone:", value: getFormattedPhoneNo(getValue(requestDetailsState, "referralSource.phone")) },
                { label: "Fax:", value: getValue(requestDetailsState, "referralSource.fax") },
            ],
            address: [
                { label: "Street 1:", value: getValue(requestDetailsState, "referralSource.street1") },
                { label: "Street 2:", value: getValue(requestDetailsState, "referralSource.street2") },
                { label: "City:", value: getValue(requestDetailsState, "referralSource.city") },
                { label: "State:", value: getValue(requestDetailsState, "referralSource.state") },
                { label: "Zip:", value: getValue(requestDetailsState, "referralSource.zip") },
            ],
        },
        {
            title: "ORDERING PHYSICIAN",
            contact: [
                { label: "Name:", value: getValue(requestDetailsState, "orderingPhysician.name") },
                { label: "NPI:", value: getValue(requestDetailsState, "orderingPhysician.npi") },
                { label: "Phone:", value: getFormattedPhoneNo(getValue(requestDetailsState, "orderingPhysician.phone")) },
                { label: "Fax:", value: getValue(requestDetailsState, "orderingPhysician.fax") },
            ],
            address: [
                { label: "Street 1:", value: getValue(requestDetailsState, "orderingPhysician.street1") },
                { label: "Street 2:", value: getValue(requestDetailsState, "orderingPhysician.street2") },
                { label: "City:", value: getValue(requestDetailsState, "orderingPhysician.city") },
                { label: "State:", value: getValue(requestDetailsState, "orderingPhysician.state") },
                { label: "Zip:", value: getValue(requestDetailsState, "orderingPhysician.zip") },
            ],
        },
        {
            title: "FOLLOWING PHYSICIAN",
            contact: [
                { label: "Name:", value: getValue(requestDetailsState, "followingPhysician.name") },
                { label: "NPI:", value: getValue(requestDetailsState, "followingPhysician.npi") },
                { label: "Phone:", value: getFormattedPhoneNo(getValue(requestDetailsState, "followingPhysician.phone")) },
                { label: "Fax:", value: getValue(requestDetailsState, "followingPhysician.fax") },
            ],
            address: [
                { label: "Street 1:", value: getValue(requestDetailsState, "followingPhysician.street1") },
                { label: "Street 2:", value: getValue(requestDetailsState, "followingPhysician.street2") },
                { label: "City:", value: getValue(requestDetailsState, "followingPhysician.city") },
                { label: "State:", value: getValue(requestDetailsState, "followingPhysician.state") },
                { label: "Zip:", value: getValue(requestDetailsState, "followingPhysician.zip") },
            ],
        },
    ];

    useEffect(() => {
        if (referralId !== null && referralId > 0) {
            getRequestDetails(referralId);
        }
    }, [referralId]);

    const renderRow = (fields: { label: string; value: string | null | undefined }[]) => (
        <Grid size={12} sx={renderRowStyle}>
            {fields.map((field) => (
                <Grid key={field.label} size={2} sx={BottomGridMiddleContainerField}>
                    <ReadOnlyField label={field.label} value={field.value ?? ""} />
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Grid container sx={OuterBodyGrid}>
            <Grid size={12} sx={TopGrid}>
                {sections.map((section) => (
                    <React.Fragment key={section.title}>
                        <Box pb={"1rem"} pt={"1rem"}>
                            <Typography
                                fontWeight={fontWeight.Weight[5]}
                                color={colors.black[1]}
                                sx={LabelHeader}
                            >
                                {section.title}
                            </Typography>
                        </Box>
                        {renderRow(section.contact)}
                        {renderRow(section.address)}

                        {section.title !== sections[sections.length - 1].title && (
                            <Divider orientation="horizontal" flexItem sx={{ margin: "0rem 1rem"}}/>
                        )}

                    </React.Fragment>
                ))}
            </Grid>
        </Grid >
    );
};

export default RequestDetails;
